import { createPortal } from 'react-dom';

import {
  IconChevronDownRound,
  IconChevronUpRound,
} from '@/components/apps/ui/icon';

import SelectMenu from './SelectMenu';
import { appsSelectAppearance } from './styles/selectStyles';
import type { SelectProps } from './types/SelectProps';
import { useSelectController } from './useSelectController';

export default function Select({
  name,
  options,
  value,
  defaultValue = null,
  defaultMenuIsOpen = false,
  placeholder = '값을 입력해주세요.',
  dropDownSize = 'md',
  menuPlacement = 'auto',
  rightAligned = false,
  styleWidthSize = '100%',
  portalZIndex = 899,
  isReadOnly = false,
  isDisabled = false,
  isRequired = false,
  showSelectedIndicator = false,
  hasError = false,
  className,
  instanceId,
  inputId,
  appearance = appsSelectAppearance,
  portalTarget,
  onChange,
  onBlur,
  ref,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: SelectProps) {
  const controller = useSelectController({
    appearance,
    defaultMenuIsOpen,
    defaultValue,
    dropDownSize,
    inputId,
    instanceId,
    isDisabled,
    isReadOnly,
    menuPlacement,
    onBlur,
    onChange,
    options,
    portalTarget,
    ref,
    rightAligned,
    value,
  });
  const {
    activeIndex,
    closeMenu,
    containerRef,
    focusTrigger,
    getOptionId,
    handleBlur,
    handleKeyDown,
    isInteractive,
    isOpen,
    isSmallSize,
    listboxId,
    listRef,
    menuState,
    openMenu,
    optionRefs,
    positionMenu,
    selectedIndex,
    selectedOption,
    setActiveIndex,
    triggerRef,
  } = controller;

  const visualState = {
    hasError,
    isDisabled,
    isInteractive,
    isOpen,
    isReadOnly,
  };
  const ChevronIcon = isOpen ? IconChevronUpRound : IconChevronDownRound;
  const menu = isOpen ? (
    <SelectMenu
      activeIndex={activeIndex}
      appearance={appearance}
      ariaLabel={ariaLabel}
      ariaLabelledBy={ariaLabelledBy}
      getOptionId={getOptionId}
      isSmallSize={isSmallSize}
      listboxId={listboxId}
      listRef={listRef}
      menuState={menuState}
      optionRefs={optionRefs}
      options={options}
      portalZIndex={portalZIndex}
      positionMenu={positionMenu}
      selectOption={controller.selectOption}
      selectedIndex={selectedIndex}
      showSelectedIndicator={showSelectedIndicator}
      setActiveIndex={setActiveIndex}
    />
  ) : null;

  return (
    <div
      ref={containerRef}
      className={`${appearance.container(visualState)} ${className ?? ''}`}
      style={{ width: styleWidthSize }}
    >
      <button
        type="button"
        id={inputId}
        ref={triggerRef}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-activedescendant={
          isOpen && activeIndex >= 0 ? getOptionId(activeIndex) : undefined
        }
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-required={isRequired || undefined}
        aria-readonly={isReadOnly || undefined}
        disabled={isDisabled}
        onClick={() => {
          focusTrigger();
          if (isOpen) closeMenu();
          else openMenu();
        }}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        className={appearance.control(visualState)}
      >
        <span
          className={appearance.value({
            ...visualState,
            hasValue: !!selectedOption,
          })}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className={appearance.indicators(visualState)}>
          <span className={appearance.indicatorIcon(visualState)}>
            <ChevronIcon size={appearance.indicatorIconSize} />
          </span>
        </span>
      </button>

      <input
        type="hidden"
        name={name}
        value={selectedOption ? String(selectedOption.value) : ''}
      />

      {portalTarget && menu ? createPortal(menu, portalTarget) : menu}
    </div>
  );
}
